import type { OtpForm, PinLoginForm } from "@/stores/user"
import { useUserStore } from "@/stores/user"
import type { AxiosResponse } from "@/plugins/axios"
import type { App } from "vue"
import { requestPermissions } from "@/plugins/push"
import { roles } from "@/enums/roles"

export interface Auth {
  user: App.Models.AuthUser | null
  hasRole: (role: string) => boolean
  sendOtp: (form: OtpForm) => Promise<AxiosResponse<{ code?: string; mode: 'login' | 'signup' | 'set_password' | 'reset_password' }>>
  sendPasswordResetOtp: (phone: string) => Promise<AxiosResponse<{ code?: string; mode: 'reset_password' }>>

  // URL-based authentication methods
  loginWithPassword: (form: { phone: string; password: string }) => Promise<AxiosResponse<{ access_token: string }>>
  signupWithOtp: (form: { otp: string; password: string; password_confirmation: string; phone: string }) => Promise<AxiosResponse<{ access_token: string }>>
  loginWithOtp: (form: { otp: string; password: string; password_confirmation: string; phone: string }) => Promise<AxiosResponse<{ access_token: string }>>
  resetPassword: (form: { otp: string; password: string; password_confirmation: string; phone: string }) => Promise<AxiosResponse<{ access_token: string }>>
  pinLogin: (form: PinLoginForm) => Promise<AxiosResponse<{ access_token: string; pin_trust_token?: string }>>
  verifyPinTrust: () => Promise<{ valid: boolean; userId?: number }>
  clearPinTrust: () => Promise<void>
  loadUser: (force?: boolean) => Promise<App.Models.AuthUser | null>
  logout: () => Promise<void>
  loggedIn: boolean
  impersonate: (userId: number) => Promise<AxiosResponse<{ access_token: string }>>
  stopImpersonating: () => Promise<AxiosResponse<{ access_token: string }>>
  roleAtLeast: (role: string, requestingUser?: App.Models.AuthUser | null) => boolean
}

export const $auth: Auth = {
  get user(): App.Models.AuthUser | null {
    const userStore = useUserStore()
    return userStore.user
  },
  set user(user: App.Models.AuthUser | null) {
    const userStore = useUserStore()
    userStore.user = user
  },
  hasRole(role: string): boolean {
    return this.user?.role === role
  },
  sendOtp(form: OtpForm) {
    const userStore = useUserStore()
    return userStore.sendOtp(form)
  },

  sendPasswordResetOtp(phone: string) {
    const userStore = useUserStore()
    return userStore.sendPasswordResetOtp(phone)
  },

  // Cursor: URL-based authentication methods
  async loginWithPassword(form: { phone: string; password: string }) {
    const userStore = useUserStore()
    const response = await userStore.loginWithPassword(form)
    requestPermissions()
    return response
  },

  async signupWithOtp(form: { otp: string; password: string; password_confirmation: string; phone: string }) {
    const userStore = useUserStore()
    const response = await userStore.signupWithOtp(form)
    requestPermissions()
    return response
  },

  async loginWithOtp(form: { otp: string; password: string; password_confirmation: string; phone: string }) {
    const userStore = useUserStore()
    const response = await userStore.loginWithOtp(form)
    requestPermissions()
    return response
  },

  async resetPassword(form: { otp: string; password: string; password_confirmation: string; phone: string }) {
    const userStore = useUserStore()
    const response = await userStore.resetPassword(form)
    requestPermissions()
    return response
  },

  async pinLogin(form: PinLoginForm) {
    const userStore = useUserStore()
    const response = await userStore.pinLogin(form)
    requestPermissions()
    return response
  },
  async verifyPinTrust() {
    const userStore = useUserStore()
    const result = await userStore.verifyPinTrust()
    if (result.valid) {
      requestPermissions()
    }
    return result
  },
  clearPinTrust() {
    const userStore = useUserStore()
    return userStore.clearPinTrust()
  },
  async loadUser(force: boolean = false) {
    const userStore = useUserStore()
    const user = await userStore.loadUser(force)
    if (user) {
      requestPermissions()
    }
    return user
  },
  logout() {
    const userStore = useUserStore()
    return userStore.logout()
  },
  get loggedIn() {
    return !!this.user
  },
  async impersonate(userId: number) {
    const userStore = useUserStore()
    return await userStore.impersonate(userId)
  },
  async stopImpersonating() {
    const userStore = useUserStore()
    return await userStore.stopImpersonating()
  },
  roleAtLeast(role: string): boolean {
    if (!this.user) {
      return false
    }

    const rolesHierarchy = {
      [roles.LIBRA_ADMIN]: 0,
      [roles.ACCOUNT_OWNER]: 1,
      [roles.AUTONOMOUS]: 2,
      [roles.NON_AUTONOMOUS]: 3,
    }

    return rolesHierarchy[this.user.role] <= rolesHierarchy[role]
  },
}

export default {
  install(app: App) {
    app.config.globalProperties.$auth = $auth
  }
}
